import { get, has, isEmpty, find, remove } from 'lodash';
import { transformValuesToString, encodeTextAnswers } from './utils';
import { QUESTION_MATRIX_SINGLE } from './constants';
import request from './request';

Pollfish.sanity = (function sanity() {
  let pendingQuestions = [];
  let trapQuestions = [];
  let answeredQuestions = [];
  let savedAnswers = [];
  let isDisqualified = false;
  let isCompleted = false;
  let answeredQuestionsRotation = -1;
  let isTrapQuestionAnswered = false;
  let finalQuestion;
  return {
    setQuestions(questions = []) {
      if (Pollfish.helper.isPlayfulFormat()) {
        // do nothing for playful format surveys
        return;
      }
      pendingQuestions = [];
      trapQuestions = [];
      // we can refrain from deleting those if we want to keep history
      savedAnswers = [];
      answeredQuestions = [];
      isTrapQuestionAnswered = false;
      finalQuestion = undefined;
      answeredQuestionsRotation = -1;

      const formattedQuestions = questions.map((question) => {
        const newQuestion = {
          ...question,
          isSanityQuestion: true,
        };
        if (question.style === QUESTION_MATRIX_SINGLE) {
          newQuestion.isTrapQuestion = true;
        }
        return newQuestion;
      });

      formattedQuestions.forEach((question) => {
        if (question.style === QUESTION_MATRIX_SINGLE) {
          trapQuestions.push(question);
        } else {
          pendingQuestions.push(question);
        }
      });
      isDisqualified = false;
      if (isEmpty(questions)) {
        isCompleted = true;
      } else {
        isCompleted = false;
      }
    },
    async fetchAdditionalQuestions() {
      if (pendingQuestions.length >= 4) {
        // we have no reason to fetch more questions since we already have enough
        return;
      }
      const additionalQuestions = await request({
        url: `/v2/device/sanity/questions?n=7&lang=${Pollfish.ui.currentLang}`,
      });
      const formattedAdditionalQuestions = additionalQuestions.map(
        (question) => ({
          ...question,
          isSanityQuestion: true,
        }),
      );
      pendingQuestions = [...pendingQuestions, ...formattedAdditionalQuestions];
    },
    reset() {
      isDisqualified = false;
      isTrapQuestionAnswered = false;
      finalQuestion = undefined;
      answeredQuestionsRotation = -1;
      if (isEmpty(pendingQuestions) && isEmpty(answeredQuestions)) {
        isCompleted = true;
      } else {
        isCompleted = false;
      }

      // we can refrain from deleting those if we want to keep history
      savedAnswers = [];
      // answeredQuestions = [];
    },
    getAllQuestions() {
      return [...pendingQuestions, ...trapQuestions, ...answeredQuestions];
    },
    getQuestions() {
      return pendingQuestions;
    },
    getAnswersCount() {
      return savedAnswers.length;
    },
    hasTrapQuestion() {
      return !isEmpty(trapQuestions);
    },
    isPending() {
      return !isCompleted;
    },
    isCompleted() {
      return isCompleted;
    },
    getNextQuestion() {
      if (isEmpty(pendingQuestions)) {
        answeredQuestionsRotation += 1;
        const rotationIndex =
          answeredQuestionsRotation % answeredQuestions.length;
        return answeredQuestions[rotationIndex];
      }
      return pendingQuestions[0];
    },
    getNextTrapQuestion() {
      // we only have one trap question currently
      return trapQuestions[0];
    },
    getQuestionByRefId(refId) {
      return find(this.getAllQuestions(), ['refId', refId]);
    },
    getQuestionById(id) {
      return find(this.getAllQuestions(), ['id', id]);
    },
    setDisqualified(bool) {
      isDisqualified = bool;
    },
    isDisqualified() {
      return isDisqualified;
    },
    setCompleted(bool) {
      isCompleted = bool;
    },
    hasAnsweredTrapQuestion() {
      return isTrapQuestionAnswered;
    },
    getFinalSanity() {
      // we needed a way to provide the sanity class with the info that
      // this is the last question
      finalQuestion = this.getNextQuestion();
      return finalQuestion;
    },
    saveAnswer(requestObject) {
      // NOTE: we can check here if a user has already answered the given question!
      // or at least if we know it since we keep it in-memory
      const questions = this.getAllQuestions();
      const answeredQuestionId = parseInt(requestObject.q_id, 10);
      const answeredQuestionRefId = requestObject.q_ref_id;
      let answeredQuestion;
      if (answeredQuestionRefId) {
        answeredQuestion = find(questions, ['refId', answeredQuestionRefId]);
      } else {
        answeredQuestion = find(questions, ['id', answeredQuestionId]);
      }
      if (!answeredQuestion) {
        throw new Error('Missing answered question from request object');
      }

      // sanity flow is completed when the trap question is answered
      // correctly (for this flow. We will be changing this later)
      if (answeredQuestion.isTrapQuestion) {
        isTrapQuestionAnswered = true;
      }

      if (finalQuestion && answeredQuestion.refId === finalQuestion.refId) {
        isCompleted = true;
      }
      // we save these answers so that the backend will receive them all together
      // but the backend expects their values as strings s_id, ttc etc
      savedAnswers.push(
        encodeTextAnswers(transformValuesToString(requestObject)),
      );
      answeredQuestions.push(answeredQuestion);

      const qualifyingAnswer = find(answeredQuestion.answers, [
        'screener_limit',
        1,
      ]);
      if (!qualifyingAnswer || !has(qualifyingAnswer, 'id')) {
        Raven.captureMessage('Qualifying answer missing', {
          extra: {
            ...Pollfish.helper.getRedirectData(),
            question: answeredQuestion,
            requestObject,
          },
        });
      }

      // NOTE: this is here for investigation reasons. We sometimes didn't
      // have qualifyingAnswer ids and we thought that they might be numbers
      const qualifyingAnswerId = get(qualifyingAnswer, 'id', '').toString();
      remove(pendingQuestions, ['id', answeredQuestionId]);
      // send the selected answer to the server
      Pollfish.mobile.interface.sendToServer(
        '/v2/device/set/sanity/question/answered',
        {
          ...requestObject,
          qualifying_answer_id: qualifyingAnswerId,
          // emojis need to be encoded unfortunately
          qualifying_answer_body: encodeURIComponent(qualifyingAnswer.body),
        },
        true,
      );
    },
    getStoredAnswers() {
      return savedAnswers;
    },
  };
})();

export default Pollfish.sanity;
